

import { useEffect, useState, useCallback, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, TextField, Button, CircularProgress, Alert,
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Checkbox,
  MenuItem, Grid, Stack, IconButton, FormGroup, Divider,
  Tooltip
} from '@mui/material';
import { Add, Delete, Save, Edit, Cancel, InfoOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getJobForApplication, submitApplication } from '../../../../services/api';

const getSubmitApplicationErrorMessage = (error) => {
  const status = Number(error?.response?.status);
  const responseError = String(error?.response?.data?.error || '').trim();
  const responseMessage = String(error?.response?.data?.message || '').trim();
  const normalizedResponse = `${responseError} ${responseMessage}`.toLowerCase();

  if (
    normalizedResponse.includes('candidatura já finalizada')
    || normalizedResponse.includes('você já se candidatou')
    || (status === 500 && normalizedResponse.includes('erro ao registrar candidatura'))
  ) {
    return 'Este e-mail já possui uma candidatura registrada para esta vaga.';
  }

  return responseMessage || responseError || error?.message || 'Não foi possível enviar candidatura.';
};

/**
 * Componente customizado para a pergunta do tipo 'experience'.
 * Permite ao usuário adicionar, editar e remover múltiplas experiências de trabalho
 * em um formulário dinâmico.
 * @param {{question: object, value: Array<object>, onChange: function}} props
 * @param {object} props.question - O objeto da pergunta vindo da API.
 * @param {Array<object>} props.value - O valor atual (lista de experiências) do estado do formulário.
 * @param {function} props.onChange - Callback para atualizar o estado do formulário no componente pai.
 */
const ExperienceQuestion = ({ question, value, onChange }) => {
  const [experiences, setExperiences] = useState([]);

  // Popula o estado local quando o 'value' (vindo do formulário) é carregado
  useEffect(() => {
    const initialExperiences = Array.isArray(value)
      ? value.map((exp, index) => ({
          id: `saved-${index}-${Date.now()}`, // ID único para o estado do React
          data: exp,
          status: 'display', // 'display', 'edit', 'new'
        }))
      : [];
    setExperiences(initialExperiences);
  }, [value]);

  /**
   * Filtra as experiências salvas ('display') e envia os dados puros
   * para o formulário principal.
   * @param {Array<object>} updatedExperiences - A lista completa do estado local (com status 'new', 'edit', 'display').
   */
  const triggerOnChange = (updatedExperiences) => {
    const savedData = updatedExperiences
      .filter(exp => exp.status === 'display')
      .map(exp => exp.data);
    onChange(savedData);
  };

  /**
   * Adiciona um novo formulário de experiência em branco ao estado,
   * se nenhum outro já estiver em modo 'new'.
   */
  const handleAddNewForm = () => {
     const hasNewUnsaved = experiences.some(exp => exp.status === 'new');
     if (hasNewUnsaved) {
         toast.info("Salve ou cancele a experiência atual antes de adicionar uma nova.");
         return;
     }
    setExperiences(prev => [
      ...prev,
      { id: `new-${Date.now()}`, data: { company_name: '', start_date: '', end_date: '' }, status: 'new' }
    ]);
  };

  /**
   * Atualiza os dados de um formulário de experiência (em modo 'new' ou 'edit').
   * @param {string} id - O ID de estado temporário da experiência.
   * @param {string} field - O campo (ex: 'company_name').
   * @param {string} fieldValue - O novo valor.
   */
  const handleDataChange = (id, field, fieldValue) => {
    setExperiences(prev => prev.map(exp =>
      exp.id === id ? { ...exp, data: { ...exp.data, [field]: fieldValue } } : exp
    ));
  };

  /**
   * Coloca uma experiência existente no modo 'edit'.
   * @param {string} id - O ID de estado da experiência.
   */
  const handleEdit = (id) => {
    setExperiences(prev => prev.map(exp =>
      exp.id === id ? { ...exp, status: 'edit' } : exp
    ));
  };

  /**
   * Salva uma experiência (modo 'new' ou 'edit'), validando os dados
   * e atualizando o formulário principal.
   * @param {string} id - O ID de estado da experiência.
   */
  const handleSave = (id) => {
    const experienceToSave = experiences.find(exp => exp.id === id);
    if (!experienceToSave) return;
    if (!experienceToSave.data.company_name || !experienceToSave.data.start_date) {
      toast.warning('Preencha o nome da empresa e a data de início.');
      return;
    }
     if (experienceToSave.data.end_date && experienceToSave.data.start_date > experienceToSave.data.end_date) {
         toast.warning('A data de fim não pode ser anterior à data de início.');
         return;
     }
    const updatedExperiences = experiences.map(exp =>
      exp.id === id ? { ...exp, status: 'display' } : exp
    );
    setExperiences(updatedExperiences);
    triggerOnChange(updatedExperiences);
  };

  /**
   * Cancela a edição ou adição de uma experiência.
   * @param {string} id - O ID de estado da experiência.
   * @param {'new'|'edit'} originalStatus - O status original antes do cancelamento.
   */
  const handleCancel = (id, originalStatus) => {
    if (originalStatus === 'new') {
        handleDelete(id); // Remove se era novo
    } else {
        // Apenas volta para display (não reverte dados)
        setExperiences(prev => prev.map(exp =>
            exp.id === id ? { ...exp, status: 'display' } : exp
        ));
    }
  };

  /**
   * Remove uma experiência da lista e atualiza o formulário principal.
   * @param {string} id - O ID de estado da experiência.
   */
  const handleDelete = (id) => {
    const updatedExperiences = experiences.filter(exp => exp.id !== id);
    setExperiences(updatedExperiences);
    triggerOnChange(updatedExperiences);
  };

  /**
   * Formata a data YYYY-MM-DD para pt-BR para exibição.
   * @param {string} dateString - Data no formato 'YYYY-MM-DD'.
   * @returns {string} - Data formatada ou 'Atual'.
   */
   const formatDateDisplay = (dateString) => {
       if (!dateString) return 'Atual';
       try {
           // Adiciona T00:00:00 para tratar como local (ou UTC se preferir)
           const date = new Date(dateString + 'T00:00:00');
           return date.toLocaleDateString('pt-BR');
       } catch { return dateString; }
   };

  return (
    <FormControl component="fieldset" fullWidth required={!!question.required}>
      <FormLabel component="legend" sx={{ mb: 1 }}>{question.label}</FormLabel>
      <Stack spacing={2} mt={1}>
        {experiences.map((exp) => (
          <Fragment key={exp.id}>
            {exp.status === 'display' && (
              <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography fontWeight="bold">{exp.data.company_name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateDisplay(exp.data.start_date)} – {formatDateDisplay(exp.data.end_date)}
                  </Typography>
                </Box>
                <Box>
                   <Tooltip title="Editar"><IconButton onClick={() => handleEdit(exp.id)} color="primary" size="small" aria-label="Editar experiência"><Edit fontSize="small"/></IconButton></Tooltip>
                   <Tooltip title="Excluir"><IconButton onClick={() => handleDelete(exp.id)} color="error" size="small" aria-label="Excluir experiência"><Delete fontSize="small"/></IconButton></Tooltip>
                </Box>
              </Paper>
            )}
            {(exp.status === 'edit' || exp.status === 'new') && (
              <Paper sx={{ p: 2, border: exp.status === 'new' ? '1px dashed #ccc' : '1px solid', borderColor: 'primary.main' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormLabel htmlFor={`experience-${exp.id}-company-name`} required sx={{ whiteSpace: 'normal' }}>
                        Nome da Empresa
                      </FormLabel>
                      <TextField
                        id={`experience-${exp.id}-company-name`}
                        value={exp.data.company_name}
                        onChange={(e) => handleDataChange(exp.id, 'company_name', e.target.value)}
                        fullWidth
                        size="small"
                        required
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormLabel htmlFor={`experience-${exp.id}-start-date`} required sx={{ whiteSpace: 'normal' }}>
                        Data de Início
                      </FormLabel>
                      <TextField
                        id={`experience-${exp.id}-start-date`}
                        type="date"
                        value={exp.data.start_date}
                        onChange={(e) => handleDataChange(exp.id, 'start_date', e.target.value)}
                        fullWidth
                        size="small"
                        required
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <FormLabel
                        htmlFor={`experience-${exp.id}-end-date`}
                        sx={{ whiteSpace: 'normal', display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        Data de Fim (opcional)
                        <Tooltip title="Deixe em branco se este for o emprego atual." arrow>
                          <Box
                            component="span"
                            tabIndex={0}
                            aria-label="Informação sobre a data de fim"
                            sx={{ display: 'inline-flex', color: 'text.secondary', cursor: 'help' }}
                          >
                            <InfoOutlined sx={{ fontSize: 17 }} />
                          </Box>
                        </Tooltip>
                      </FormLabel>
                      <TextField
                        id={`experience-${exp.id}-end-date`}
                        type="date"
                        value={exp.data.end_date}
                        onChange={(e) => handleDataChange(exp.id, 'end_date', e.target.value)}
                        fullWidth
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                   <Button onClick={() => handleCancel(exp.id, exp.status)} startIcon={<Cancel />} size="small">Cancelar</Button>
                   <Button onClick={() => handleSave(exp.id)} variant="contained" color="primary" startIcon={<Save />} size="small">Salvar Experiência</Button>
                 </Stack>
              </Paper>
            )}
             { experiences.length > 1 && <Divider light />}
          </Fragment>
        ))}
        <Button startIcon={<Add />} onClick={handleAddNewForm}>Adicionar Nova Experiência</Button>
      </Stack>
       {question.help_text && <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display:'block' }}>{question.help_text}</Typography>}
    </FormControl>
  );
};


/**
 * Componente de página (pública) para o candidato iniciar sua aplicação em uma vaga.
 * Busca a vaga e as perguntas da primeira etapa, e submete os dados do
 * candidato e suas respostas.
 */
export default function JobApplication() {
  const { jobId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [candidateInfo, setCandidateInfo] = useState({ full_name: '', email: '', phone: '' });
  const [answers, setAnswers] = useState({});

  /**
   * Busca os dados da vaga e as perguntas da primeira etapa.
   */
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getJobForApplication(jobId);
      if (!data || !data.job) {
        setError('Vaga não encontrada ou indisponível.');
        return;
      }
      setJob(data.job);
      setQuestions(Array.isArray(data.questions) ? data.questions : []);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
        err?.message ||
        'Vaga não encontrada ou indisponível.'
      );
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  /**
   * Atualiza o estado 'answers' para uma pergunta.
   * @param {string} questionId - ID da pergunta.
   * @param {*} value - Novo valor da resposta.
   */
  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  /**
   * Manipulador de submissão do formulário.
   * Envia os dados do candidato e as respostas para a API.
   * Redireciona para a página de status (se não reprovado) ou para a página
   * de sucesso genérica.
   * @param {Event} e - Evento do formulário.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await submitApplication({ jobId, candidateInfo, answers });

      // Redireciona para a página de status em QUALQUER decisão que
      // não seja 'rejected', desde que tenhamos um ID.
      if (result?.applicationId && result?.decision !== 'rejected') {
        toast.success('Candidatura recebida! Redirecionando...');
        navigate(`/cv/status/${result.applicationId}`);
      } else {
        // Fallback para a tela de sucesso genérica (ou tela de rejeição, se 'rejected')
        navigate('/cv/application-success');
      }

    } catch (err) {
      toast.error(getSubmitApplicationErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Renderiza o componente de input apropriado com base no tipo da pergunta.
   * @param {object} q - O objeto da pergunta vindo da API.
   * @returns {JSX.Element}
   */
  const renderQuestion = (q) => {
    const value = answers[q.id];
    const questionType = String(q.type).toLowerCase();
    const inputId = `question-${q.id}`;

    switch (questionType) {
      case 'text':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormLabel htmlFor={inputId} required={!!q.required} sx={{ whiteSpace: 'normal' }}>
              {q.label}
            </FormLabel>
            <TextField
              id={inputId}
              fullWidth
              value={value || ''}
              onChange={(e) => handleAnswerChange(q.id, e.target.value)}
              required={!!q.required}
              helperText={q.help_text}
              multiline
              minRows={3}
              maxRows={8}
            />
          </Box>
        );

      case 'number':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormLabel htmlFor={inputId} required={!!q.required} sx={{ whiteSpace: 'normal' }}>
              {q.label}
            </FormLabel>
            <TextField
              id={inputId}
              fullWidth
              type="number"
              value={value || ''}
              onChange={(e) => handleAnswerChange(q.id, e.target.value)}
              required={!!q.required}
              helperText={q.help_text}
            />
          </Box>
        );

      case 'boolean':
        return (
          <FormControl component="fieldset" required={!!q.required}>
            <FormLabel component="legend">{q.label}</FormLabel>
            <RadioGroup
              row
              value={value === true ? 'true' : value === false ? 'false' : ''}
              onChange={(e) => handleAnswerChange(q.id, e.target.value === 'true')}
            >
              <FormControlLabel value="true" control={<Radio />} label="Sim" />
              <FormControlLabel value="false" control={<Radio />} label="Não" />
            </RadioGroup>
            {q.help_text && <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>{q.help_text}</Typography>}
          </FormControl>
        );

      case 'select':
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormLabel htmlFor={inputId} required={!!q.required} sx={{ whiteSpace: 'normal' }}>
              {q.label}
            </FormLabel>
            <TextField
              id={inputId}
              select
              fullWidth
              value={value || ''} // value deve ser o ID da opção
              onChange={(e) => handleAnswerChange(q.id, e.target.value)}
              required={!!q.required}
              helperText={q.help_text}
              SelectProps={{ displayEmpty: true }}
            >
              {!q.required && <MenuItem value=""><em>Selecione...</em></MenuItem>}
              {(q.options || []).map((opt) => (
                <MenuItem key={opt.id} value={opt.id}> {/* value={opt.id} */}
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        );

      case 'multiselect': {
        const selectedValues = new Set(Array.isArray(value) ? value : []);
        const handleChange = (optionId) => {
          const newValues = new Set(selectedValues);
          if (newValues.has(optionId)) { newValues.delete(optionId); }
          else { newValues.add(optionId); }
          handleAnswerChange(q.id, Array.from(newValues)); // Salva array de IDs
        };
        return (
          <FormControl component="fieldset" fullWidth required={!!q.required}>
            <FormLabel component="legend">{q.label}</FormLabel>
            <FormGroup>
              {(q.options || []).map((opt) => (
                <FormControlLabel
                  key={opt.id}
                  control={ <Checkbox checked={selectedValues.has(opt.id)} onChange={() => handleChange(opt.id)} /> }
                  label={opt.label}
                />
              ))}
            </FormGroup>
            {q.help_text && <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>{q.help_text}</Typography>}
          </FormControl>
        );
      }

      case 'likert':
      case 'escala (1-5)': // Fallback
        return (
          <FormControl component="fieldset" required={!!q.required}>
            <FormLabel component="legend">{q.label}</FormLabel>
            <RadioGroup
              row
              value={value || ''} // value é o número (como string)
              onChange={(e) => handleAnswerChange(q.id, e.target.value ? Number(e.target.value) : null)} // Salva número
            >
              {[1, 2, 3, 4, 5].map(num => (
                <FormControlLabel key={num} value={num.toString()} control={<Radio />} label={num.toString()} />
              ))}
            </RadioGroup>
            {q.help_text && <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>{q.help_text}</Typography>}
          </FormControl>
        );

      case 'experience':
        return (
          <ExperienceQuestion
            question={q}
            value={value}
            onChange={(newValue) => handleAnswerChange(q.id, newValue)}
          />
        );

      default:
        return (
          <Alert severity="warning">Tipo de pergunta não suportado: <b>{q.type}</b></Alert>
        );
    }
  };

  // Renderização principal
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ maxWidth: 700, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h4" gutterBottom>{job?.title || 'Vaga'}</Typography>
        {job?.area && (
          <Typography variant="subtitle1" color="text.secondary" mb={3}>
            {job.area}
          </Typography>
        )}
        <form onSubmit={handleSubmit}>
          <Typography variant="h6" mb={2}>Seus Dados</Typography>
          <Grid container spacing={2} mb={4}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormLabel htmlFor="candidate-full-name" required sx={{ whiteSpace: 'normal' }}>
                  Nome Completo
                </FormLabel>
                <TextField
                  id="candidate-full-name"
                  fullWidth
                  value={candidateInfo.full_name}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, full_name: e.target.value })}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormLabel htmlFor="candidate-email" required sx={{ whiteSpace: 'normal' }}>
                  Email
                </FormLabel>
                <TextField
                  id="candidate-email"
                  fullWidth
                  type="email"
                  value={candidateInfo.email}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, email: e.target.value })}
                  required
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <FormLabel htmlFor="candidate-phone" required sx={{ whiteSpace: 'normal' }}>
                  Telefone
                </FormLabel>
                <TextField
                  id="candidate-phone"
                  fullWidth
                  value={candidateInfo.phone}
                  onChange={(e) => setCandidateInfo({ ...candidateInfo, phone: e.target.value })}
                  required
                />
              </Box>
            </Grid>
          </Grid>
          
          <Typography variant="h6" mb={2}>Perguntas Iniciais</Typography>
          
          {Array.isArray(questions) && questions.length > 0 && (
             <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              * Pergunta obrigatória.
             </Typography>
          )}

          {Array.isArray(questions) && questions.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Esta vaga ainda não possui perguntas publicadas. Preencha seus dados e envie sua candidatura.
            </Alert>
          )}
          
          <Box display="flex" flexDirection="column" gap={3}>
            {(questions || []).map((q) => (
              <Box key={q.id}>{renderQuestion(q)}</Box>
            ))}
            <Button type="submit" variant="contained" size="large" disabled={submitting}>
              {submitting ? 'Enviando...' : 'Enviar Candidatura'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
